
import { useState, useEffect } from "react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { RefreshCw } from "lucide-react";

interface ResourceCardProgressProps {
  progress: number | null;
  isCompleted: boolean;
  completedAt?: string | null;
  isCalculatingProgress: boolean;
  resourceId?: string;
  refreshProgress?: () => void;
  isTrainee?: boolean;
}

export function ResourceCardProgress({ 
  progress, 
  isCompleted, 
  completedAt,
  isCalculatingProgress,
  resourceId,
  refreshProgress,
  isTrainee = true
}: ResourceCardProgressProps) {
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Don't render for non-trainees (admins)
  if (!isTrainee) {
    return null;
  }
  
  // Auto-refresh on mount
  useEffect(() => {
    if (refreshProgress && progress !== 100) {
      refreshProgress();
    }
  }, [refreshProgress, progress]);
  
  const handleRefresh = () => {
    if (refreshProgress) {
      setIsSyncing(true);
      refreshProgress();
      // Turn off the syncing state after a delay
      setTimeout(() => setIsSyncing(false), 1000);
    }
  };
  
  return (
    <div className="mt-4 space-y-2">
      <div className="flex justify-between text-sm text-muted-foreground">
        <span>{isCompleted ? 'Completed' : 'Progress'}</span>
        <div className="flex items-center gap-1">
          <span className={cn(
            isCompleted ? "text-green-600 font-semibold" : "",
            isCalculatingProgress ? "animate-pulse" : ""
          )}>
            {progress ?? 0}%
          </span>
          {refreshProgress && (
            <button 
              className="text-gray-400 hover:text-gray-600"
              onClick={handleRefresh}
              disabled={isSyncing}
            >
              <RefreshCw size={14} className={cn(isSyncing && "animate-spin")} />
            </button>
          )}
        </div>
      </div>
      <Progress 
        value={progress ?? 0} 
        className={cn(
          "h-3 w-full",
          progress === 100 ? "bg-green-100" : "",
          progress === 0 ? "bg-slate-100" : ""
        )}
      />
    </div>
  );
}
